import { useId } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipContentProps } from "recharts";
import type { CompetitorKpiChartPoint } from "@nexus/contracts";
import { useTranslation } from "react-i18next";
import { formatBrandName, formatCountValue } from "../../lib/format";
import { formatCompetitorChartDate } from "../../lib/competitor-chart-date";

const SERIES = [
  { key: "you", color: "var(--chart-1)" },
  { key: "mainCompetitor", color: "var(--chart-2)" },
  { key: "benchmark", color: "#94A3B8" },
] as const;

const GRADIENT_TOP_OPACITY = 0.25;

function seriesGradientId(instanceId: string, key: SeriesKey): string {
  return `competitor-trend-gradient-${instanceId}-${key}`;
}

type SeriesKey = (typeof SERIES)[number]["key"];

type ChartRow = CompetitorKpiChartPoint & { label: string };

const CHART_AXIS_TICK = { fontSize: 11, fill: "var(--muted-foreground)" } as const;

interface CompetitorKpiTrendChartProps {
  points: CompetitorKpiChartPoint[];
  youLabel: string;
  mainCompetitorLabel: string;
  benchmarkLabel: string;
}

function TrendTooltip({
  active,
  payload,
  label,
  seriesLabels,
}: TooltipContentProps<number, string> & {
  seriesLabels: Record<SeriesKey, string>;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs shadow-md">
      <p className="mb-1.5 font-medium text-foreground">{String(label)}</p>
      {payload.map((entry) => {
        const key = entry.dataKey as SeriesKey;
        return (
          <p key={key} className="tabular-nums text-muted-foreground">
            <span className="font-medium text-foreground">{seriesLabels[key]}:</span>{" "}
            {formatCountValue(Number(entry.value ?? 0))}
          </p>
        );
      })}
    </div>
  );
}

export function CompetitorKpiTrendChart({
  points,
  youLabel,
  mainCompetitorLabel,
  benchmarkLabel,
}: CompetitorKpiTrendChartProps) {
  const { t } = useTranslation();
  // React ids contain colons, which are CSS combinators and break `url(#...)`.
  const chartInstanceId = useId().replace(/:/g, "");

  const seriesLabels: Record<SeriesKey, string> = {
    you: formatBrandName(youLabel),
    mainCompetitor: formatBrandName(mainCompetitorLabel),
    benchmark: benchmarkLabel,
  };

  const visibleSeries = SERIES.filter((series) => {
    if (series.key !== "mainCompetitor") return true;
    return points.some((point) => point.mainCompetitor > 0);
  });

  const chartData: ChartRow[] = points.map((point) => ({
    ...point,
    label: formatCompetitorChartDate(point.date),
  }));

  if (chartData.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
        {t("dashboard.competitorTrendDrawer.empty")}
      </div>
    );
  }

  return (
    <div className="h-[280px] w-full" data-competitor-kpi-trend-chart>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <defs>
            {visibleSeries.map((series) => (
              <linearGradient
                key={series.key}
                id={seriesGradientId(chartInstanceId, series.key)}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={series.color} stopOpacity={GRADIENT_TOP_OPACITY} />
                <stop offset="100%" stopColor={series.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: "var(--border)" }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={40}
            tickFormatter={(v: number) => formatCountValue(v)}
          />
          <Tooltip
            content={(props) => (
              <TrendTooltip
                {...(props as TooltipContentProps<number, string>)}
                seriesLabels={seriesLabels}
              />
            )}
          />
          <Legend
            verticalAlign="top"
            align="right"
            iconType="circle"
            iconSize={8}
            formatter={(value: string) => (
              <span className="text-xs text-muted-foreground">{value}</span>
            )}
          />
          {visibleSeries.map((series) => (
            <Area
              key={series.key}
              type="monotone"
              dataKey={series.key}
              name={seriesLabels[series.key]}
              stroke={series.color}
              strokeWidth={2}
              fill={`url(#${seriesGradientId(chartInstanceId, series.key)})`}
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0 }}
              connectNulls
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
